// FEAT-024 — Allocation Audit History Table (SPEC-RG-01 AC-3 / SPEC-RG-02 AC-7)
//
// 두 가지 use case 를 같은 컴포넌트로 처리:
//   - kind="grant"     : HQ → Corp 부여 이력 (operation='grant', target='self')
//   - kind="dealer"    : Corp ↔ Dealer 할당/회수 이력 (operation∈{allocate,revoke})
//
// audit 레코드 shape:
//   { tsIso, ts, operation, target, delta_initial, delta_renewal, actor }
//
// 표시 규칙:
//   - delta_initial/renewal 의 부호로 +/- 색 구분 (allocate/grant=green, revoke=red)
//   - actor 는 adminid 또는 corpId fallback. 'hq' 는 HQ 작업.
//   - 일시는 toLocaleString() 으로 사용자 로컬 타임존 표시.

import React, { useMemo, useCallback } from 'react';
import { Translate as t } from '../../config/Translate';
import { renderHeader } from './columns';
import CompanyCell from './CompanyCell';
import useClientTable from './useClientTable';
import TablePager from './TablePager';
import SortableHeader from './SortableHeader';
// IMP-438: 날짜 포맷 SSoT — 앱 언어 locale 로 렌더(브라우저 로케일 미고정).
import { formatLicenseDate } from '../../lib/licenseHelper';

const formatDate = (tsIso, ts) => {
    const value = tsIso ?? (ts ? new Date(ts).toISOString() : null);
    if (!value) return '-';
    // IMP-438: 동작 변화 — 기존엔 파싱 실패 시 value 원문을 그대로 노출했으나
    // SSoT(formatLicenseDate) 는 '-' 를 반환한다. 원문 노출보다 '-' 가 안전하므로
    // 의도된 변화(formatLicenseDate 내부에서 이미 try/catch 처리).
    return formatLicenseDate(value);
};

const renderDelta = (delta) => {
    if (typeof delta !== 'number' || Number.isNaN(delta)) return '0';
    if (delta > 0) {
        return <span className="text-success">+{delta}</span>;
    }
    if (delta < 0) {
        return <span className="text-danger">{delta}</span>;
    }
    return <span className="text-muted">0</span>;
};

const operationLabel = (op) => {
    if (op === 'grant') return t('license.button.grant');
    if (op === 'allocate') return t('license.button.allocate');
    if (op === 'revoke') return t('license.button.revoke');
    return op || '-';
};

// 날짜 원값 → 타임스탬프 숫자(파싱 불가는 null → 정렬 시 항상 뒤).
const toTime = (tsIso, ts) => {
    const v = tsIso ?? ts;
    if (v === null || v === undefined || v === '') return null;
    const n = new Date(v).getTime();
    return Number.isNaN(n) ? null : n;
};

const DEFAULT_SORT = { key: 'date', dir: 'desc' };

const AllocationHistoryTable = ({ history = [], kind = 'dealer', loading = false, companyMap = {} }) => {
    const showTarget = kind !== 'grant';
    const colCount = 5 + (showTarget ? 1 : 0);
    // IMP-162 / IMP-166: 회사명(메인) + 보조 ID 동시 표시 — CompanyCell 공용 컴포넌트로 통일.
    const safeHistory = useMemo(() => (Array.isArray(history) ? history : []), [history]);
    const getSortValue = useCallback((row, key) => {
        switch (key) {
            case 'date': return toTime(row?.tsIso, row?.ts);
            case 'operation': return row?.operation ?? null;
            case 'company': {
                const c = row?.target ? companyMap?.[row.target] : null;
                return (c?.name || row?.target || '').toString().toLowerCase() || null;
            }
            case 'deltaInitial': return typeof row?.delta_initial === 'number' ? row.delta_initial : null;
            case 'deltaRenewal': return typeof row?.delta_renewal === 'number' ? row.delta_renewal : null;
            case 'actor': return row?.actor ? row.actor.toString().toLowerCase() : null;
            default: return null;
        }
    }, [companyMap]);
    const {
        pageRows, sortKey, sortDir, toggleSort, page, setPage, pageSize, setPageSize, total,
    } = useClientTable(safeHistory, { getSortValue, defaultSort: DEFAULT_SORT });

    const sortNode = (key, label) => (
        <SortableHeader label={label} active={sortKey === key} dir={sortDir} onToggle={() => toggleSort(key)} />
    );

    const renderCompany = (id) => <CompanyCell id={id} companyMap={companyMap} fallback="-" />;

    return (
        <div className="mb-2">
        {/* 발급 현황 탭 표와 같은 모양 — 전체 폭, 높이 제한 없음(쪽 넘김은 TablePager). */}
        <div className="table-responsive">
            <table className="table table-sm table-bordered">
                <thead className="sticky-top bg-white" style={{ zIndex: 1 }}>
                    <tr>
                        {renderHeader([
                            { key: 'date', label: 'common.field.date', style: { minWidth: 160 }, node: sortNode('date', 'common.field.date') },
                            { key: 'operation', label: 'common.label.operation', node: sortNode('operation', 'common.label.operation') },
                            // IMP-162: Dealer → Company 컬럼명 통일 (다른 리스트와 일관).
                            ...(showTarget ? [{ key: 'company', label: 'common.label.company', node: sortNode('company', 'common.label.company') }] : []),
                            { key: 'deltaInitial', label: 'license.label.deltaInitial', node: sortNode('deltaInitial', 'license.label.deltaInitial') },
                            { key: 'deltaRenewal', label: 'license.label.deltaRenewal', node: sortNode('deltaRenewal', 'license.label.deltaRenewal') },
                            { key: 'actor', label: 'common.label.actor', node: sortNode('actor', 'common.label.actor') },
                        ])}
                    </tr>
                </thead>
                <tbody className="align-middle">
                    {loading ? (
                        <tr>
                            <td colSpan={colCount} className="text-center text-muted">
                                {t('common.status.loading')}
                            </td>
                        </tr>
                    ) : total === 0 ? (
                        <tr>
                            <td colSpan={colCount} className="text-center text-muted">
                                {t('common.message.noHistory')}
                            </td>
                        </tr>
                    ) : (
                        pageRows.map((row) => (
                            <tr key={row.sk}>
                                <td>{formatDate(row.tsIso, row.ts)}</td>
                                <td>{operationLabel(row.operation)}</td>
                                {showTarget && <td>{renderCompany(row.target)}</td>}
                                <td>{renderDelta(row.delta_initial)}</td>
                                <td>{renderDelta(row.delta_renewal)}</td>
                                <td>{row.actor || '-'}</td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
        <TablePager
            total={loading ? 0 : total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
        />
        </div>
    );
};

export default AllocationHistoryTable;
